/**
 * Upload State Manager
 *
 * Centralized state management for video uploads.
 * Replaces scattered window.* global variables with a clean, testable interface.
 *
 * @module upload-state
 */

class UploadStateManager {
  constructor() {
    // Active XHR objects for cancellation
    this.xhrs = {}

    // Active EventSource objects for SSE progress tracking
    this.eventSources = {}

    // Active TUS Upload objects for cancellation
    this.tusUploads = {}

    // Retry attempt counters
    this.retries = {}

    // Paused uploads (due to network loss)
    this.paused = new Set()

    // Failed uploads waiting for network to restart
    this.waitingForNetwork = new Map()

    // User-cancelled uploads (to skip network detection)
    this.cancelling = new Set()

    // Wake locks to prevent device sleep
    this.wakeLocks = {}

    // Upload progress data
    this.progress = {}

    // Upload completion flags
    this.complete = {}

    // Network state
    this.networkState = {
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      lastOfflineTime: null,
      lastOnlineTime: Date.now(),
    }

    // Page visibility state
    this.pageVisibility = {
      isVisible: typeof document !== 'undefined' ? !document.hidden : true,
      lastHiddenTime: null,
      lastVisibleTime: Date.now(),
    }
  }

  // ========== XHR Management ==========

  /**
   * Store XHR object for an upload
   * @param {string} uploadId - Upload identifier
   * @param {XMLHttpRequest} xhr - XHR object
   */
  setXHR(uploadId, xhr) {
    this.xhrs[uploadId] = xhr
  }

  /**
   * Get XHR object for an upload
   * @param {string} uploadId - Upload identifier
   * @returns {XMLHttpRequest|undefined}
   */
  getXHR(uploadId) {
    return this.xhrs[uploadId]
  }

  /**
   * Delete XHR object for an upload
   * @param {string} uploadId - Upload identifier
   */
  deleteXHR(uploadId) {
    delete this.xhrs[uploadId]
  }

  /**
   * Get all active upload IDs
   * @returns {string[]}
   */
  getActiveUploadIds() {
    return Object.keys(this.xhrs)
  }

  /**
   * Check if upload has active XHR
   * @param {string} uploadId - Upload identifier
   * @returns {boolean}
   */
  hasActiveXHR(uploadId) {
    return !!this.xhrs[uploadId]
  }

  // ========== EventSource Management ==========

  /**
   * Store EventSource object for an upload
   * @param {string} uploadId - Upload identifier
   * @param {EventSource} eventSource - EventSource object
   */
  setEventSource(uploadId, eventSource) {
    this.eventSources[uploadId] = eventSource
  }

  /**
   * Get EventSource object for an upload
   * @param {string} uploadId - Upload identifier
   * @returns {EventSource|undefined}
   */
  getEventSource(uploadId) {
    return this.eventSources[uploadId]
  }

  /**
   * Delete EventSource object for an upload
   * @param {string} uploadId - Upload identifier
   */
  deleteEventSource(uploadId) {
    delete this.eventSources[uploadId]
  }

  /**
   * Check if upload has active EventSource
   * @param {string} uploadId - Upload identifier
   * @returns {boolean}
   */
  hasActiveEventSource(uploadId) {
    return !!this.eventSources[uploadId]
  }

  // ========== TUS Upload Management ==========

  /**
   * Store TUS Upload object for an upload
   * @param {string} uploadId - Upload identifier
   * @param {tus.Upload} tusUpload - TUS Upload object
   */
  setTusUpload(uploadId, tusUpload) {
    this.tusUploads[uploadId] = tusUpload
  }

  /**
   * Get TUS Upload object for an upload
   * @param {string} uploadId - Upload identifier
   * @returns {tus.Upload|undefined}
   */
  getTusUpload(uploadId) {
    return this.tusUploads[uploadId]
  }

  /**
   * Delete TUS Upload object for an upload
   * @param {string} uploadId - Upload identifier
   */
  deleteTusUpload(uploadId) {
    delete this.tusUploads[uploadId]
  }

  /**
   * Check if upload has active TUS Upload
   * @param {string} uploadId - Upload identifier
   * @returns {boolean}
   */
  hasActiveTusUpload(uploadId) {
    return !!this.tusUploads[uploadId]
  }

  // ========== Retry Management ==========

  /**
   * Get retry count for upload
   * @param {string} uploadId - Upload identifier
   * @returns {number}
   */
  getRetryCount(uploadId) {
    return this.retries[uploadId] || 0
  }

  /**
   * Set retry count for upload
   * @param {string} uploadId - Upload identifier
   * @param {number} count - Retry count
   */
  setRetryCount(uploadId, count) {
    this.retries[uploadId] = count
  }

  /**
   * Delete retry count for upload
   * @param {string} uploadId - Upload identifier
   */
  deleteRetryCount(uploadId) {
    delete this.retries[uploadId]
  }

  // ========== Paused Uploads ==========

  /**
   * Mark upload as paused
   * @param {string} uploadId - Upload identifier
   */
  markAsPaused(uploadId) {
    this.paused.add(uploadId)
  }

  /**
   * Unmark upload as paused
   * @param {string} uploadId - Upload identifier
   */
  unmarkAsPaused(uploadId) {
    this.paused.delete(uploadId)
  }

  /**
   * Check if upload is paused
   * @param {string} uploadId - Upload identifier
   * @returns {boolean}
   */
  isPaused(uploadId) {
    return this.paused.has(uploadId)
  }

  /**
   * Clear all paused uploads
   */
  clearPaused() {
    this.paused.clear()
  }

  // ========== Waiting for Network ==========

  /**
   * Add upload to waiting queue
   * @param {string} uploadId - Upload identifier
   * @param {Object} uploadData - Upload data to restore
   */
  addToWaitingQueue(uploadId, uploadData) {
    this.waitingForNetwork.set(uploadId, uploadData)
  }

  /**
   * Remove upload from waiting queue
   * @param {string} uploadId - Upload identifier
   */
  removeFromWaitingQueue(uploadId) {
    this.waitingForNetwork.delete(uploadId)
  }

  /**
   * Get upload data from waiting queue
   * @param {string} uploadId - Upload identifier
   * @returns {Object|undefined}
   */
  getFromWaitingQueue(uploadId) {
    return this.waitingForNetwork.get(uploadId)
  }

  /**
   * Get all uploads waiting for network
   * @returns {Array<[string, Object]>}
   */
  getWaitingQueueEntries() {
    return Array.from(this.waitingForNetwork.entries())
  }

  /**
   * Get waiting queue size
   * @returns {number}
   */
  getWaitingQueueSize() {
    return this.waitingForNetwork.size
  }

  // ========== Cancelling Uploads ==========

  /**
   * Mark upload as being cancelled
   * @param {string} uploadId - Upload identifier
   */
  markAsCancelling(uploadId) {
    this.cancelling.add(uploadId)
  }

  /**
   * Unmark upload as being cancelled
   * @param {string} uploadId - Upload identifier
   */
  unmarkAsCancelling(uploadId) {
    this.cancelling.delete(uploadId)
  }

  /**
   * Check if upload is being cancelled
   * @param {string} uploadId - Upload identifier
   * @returns {boolean}
   */
  isCancelling(uploadId) {
    return this.cancelling.has(uploadId)
  }

  // ========== Wake Lock Management ==========

  /**
   * Store wake lock for upload
   * @param {string} uploadId - Upload identifier
   * @param {WakeLockSentinel} wakeLock - Wake lock object
   */
  setWakeLock(uploadId, wakeLock) {
    this.wakeLocks[uploadId] = wakeLock
  }

  /**
   * Get wake lock for upload
   * @param {string} uploadId - Upload identifier
   * @returns {WakeLockSentinel|undefined}
   */
  getWakeLock(uploadId) {
    return this.wakeLocks[uploadId]
  }

  /**
   * Delete wake lock for upload
   * @param {string} uploadId - Upload identifier
   */
  deleteWakeLock(uploadId) {
    delete this.wakeLocks[uploadId]
  }

  // ========== Progress Management ==========

  /**
   * Set progress data for upload
   * @param {string} uploadId - Upload identifier
   * @param {Object} progressData - Progress data
   */
  setProgress(uploadId, progressData) {
    this.progress[uploadId] = progressData
  }

  /**
   * Get progress data for upload
   * @param {string} uploadId - Upload identifier
   * @returns {Object|undefined}
   */
  getProgress(uploadId) {
    return this.progress[uploadId]
  }

  /**
   * Delete progress data for upload
   * @param {string} uploadId - Upload identifier
   */
  deleteProgress(uploadId) {
    delete this.progress[uploadId]
  }

  /**
   * Get all progress entries
   * @returns {Object}
   */
  getAllProgress() {
    return this.progress
  }

  /**
   * Clear all progress data
   */
  clearAllProgress() {
    this.progress = {}
  }

  // ========== Completion Management ==========

  /**
   * Mark upload as complete
   * @param {string} uploadId - Upload identifier
   */
  markAsComplete(uploadId) {
    this.complete[uploadId] = true
  }

  /**
   * Check if upload is complete
   * @param {string} uploadId - Upload identifier
   * @returns {boolean}
   */
  isComplete(uploadId) {
    return !!this.complete[uploadId]
  }

  /**
   * Delete completion flag for upload
   * @param {string} uploadId - Upload identifier
   */
  deleteComplete(uploadId) {
    delete this.complete[uploadId]
  }

  // ========== Network State ==========

  /**
   * Update network state
   * @param {boolean} isOnline - Whether network is online
   */
  setNetworkOnline(isOnline) {
    this.networkState.isOnline = isOnline
    if (isOnline) {
      this.networkState.lastOnlineTime = Date.now()
    } else {
      this.networkState.lastOfflineTime = Date.now()
    }
  }

  /**
   * Check if network is online
   * @returns {boolean}
   */
  isNetworkOnline() {
    return this.networkState.isOnline
  }

  // ========== Page Visibility ==========

  /**
   * Update page visibility state
   * @param {boolean} isVisible - Whether page is visible
   */
  setPageVisible(isVisible) {
    this.pageVisibility.isVisible = isVisible
    if (isVisible) {
      this.pageVisibility.lastVisibleTime = Date.now()
    } else {
      this.pageVisibility.lastHiddenTime = Date.now()
    }
  }

  /**
   * Check if page is visible
   * @returns {boolean}
   */
  isPageVisible() {
    return this.pageVisibility.isVisible
  }

  /**
   * Get how long page has been hidden
   * @returns {number|null} Milliseconds, or null if not hidden
   */
  getHiddenDuration() {
    if (this.pageVisibility.isVisible || !this.pageVisibility.lastHiddenTime) {
      return null
    }
    return Date.now() - this.pageVisibility.lastHiddenTime
  }

  // ========== Cleanup ==========

  /**
   * Clean up all state for a specific upload
   * @param {string} uploadId - Upload identifier
   */
  cleanupUpload(uploadId) {
    this.deleteXHR(uploadId)
    this.deleteEventSource(uploadId)
    this.deleteTusUpload(uploadId)
    this.deleteRetryCount(uploadId)
    this.unmarkAsPaused(uploadId)
    this.removeFromWaitingQueue(uploadId)
    this.unmarkAsCancelling(uploadId)
    this.deleteWakeLock(uploadId)
    this.deleteProgress(uploadId)
    this.deleteComplete(uploadId)
  }

  /**
   * Clear all state (useful for testing)
   */
  reset() {
    this.xhrs = {}
    this.eventSources = {}
    this.tusUploads = {}
    this.retries = {}
    this.paused.clear()
    this.waitingForNetwork.clear()
    this.cancelling.clear()
    this.wakeLocks = {}
    this.progress = {}
    this.complete = {}
  }
}

// Create singleton instance
const uploadState = new UploadStateManager()

// Export singleton
export default uploadState

// Also export class for testing
export { UploadStateManager }
